if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      abortController = undefined;
      onVariantChangeUnsubscriber = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];
      cartUpdateUnsubscriber = undefined;

      constructor() {
        super();
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get productId() {
        return this.getAttribute('data-product-id');
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get productForm() {
        return this.querySelector('form[is="product-form"]');
      }

      get quantityInput() {
        return this.querySelector('quantity-input input');
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = FoxTheme.pubsub.subscribe(
          FoxTheme.pubsub.PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();

        this.currentVariant = this.getSelectedVariant(this);
        if (this.currentVariant) {
          this.updateMedia(this.currentVariant);
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
      }

      initializeProductSwapUtility() {
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;
        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;
        const viewMode = this.dataset.viewMode || 'main-product';

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage, viewMode)
            : this.handleUpdateProductInfo(productUrl, viewMode),
        });
      }

      handleSwapProduct(productUrl, updateFullPage, viewMode) {
        return (html) => {
          const quickView = html.querySelector('#MainProduct-quick-view__content');
          if (quickView && viewMode === 'quick-view') {
            html = quickView.content.cloneNode(true);
          }
          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));

          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;
            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
            HTMLUpdateUtility.viewTransition(
              document.getElementById('shopify-section-sticky-atc-bar'),
              html.getElementById('shopify-section-sticky-atc-bar'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }

          this.currentVariant = variant;
        };
      }

      handleUpdateProductInfo(productUrl, viewMode) {
        return (html) => {
          const quickView = html.querySelector('#MainProduct-quick-view__content');
          if (quickView && viewMode === 'quick-view') {
            html = quickView.content.cloneNode(true);
          }
          const variant = this.getSelectedVariant(html);

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateShareUrl(variant?.id);
          this.updateVariantInputs(variant?.id);

          if (!variant) {
            this.setUnavailable();
            return;
          } else {
            this.setAvailable();
          }

          this.updateMedia(variant);

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);

            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          updateSourceFromDestination('price');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Badges', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('PricePerItem', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Volume');

          this.updateQuantityRules(this.sectionId, this.productId, html);
          this.querySelector(`#QuantityRules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#VolumeNote-${this.dataset.section}`)?.classList.remove('hidden');

          HTMLUpdateUtility.viewTransition(
            document.querySelector(`#SizeChart-${this.sectionId}`),
            html.querySelector(`#SizeChart-${this.sectionId}`),
            this.preProcessHtmlCallbacks,
            this.postProcessHtmlCallbacks
          );

          const addButtonUpdated = html.getElementById(`ProductSubmitButton-${this.sectionId}`);
          this.toggleAddButton(
            addButtonUpdated ? addButtonUpdated.hasAttribute('disabled') : true,
            FoxTheme.variantStrings.addToCart
          );

          FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });

          function handleBespokeVariant(variant) {
              const bespokeStep = document.querySelector('.product-step-bespoke-height');
              const bespokeProperty = document.querySelector('.bespoke-height-property');
              const gateHeightSelected = document.querySelector('[data-selected-swatch-value="gate height"]').textContent.trim();
          
              if (!bespokeStep) return; // Exit if the element doesn't exist
          
              const variantTitle = variant.title.toLowerCase(); // Convert title to lowercase
          
              if (variantTitle.includes('bespoke size')) {
                  bespokeStep.classList.remove('hide-step');
                  bespokeProperty.value = '';
              } else {
                  bespokeStep.classList.add('hide-step'); // Hide the bespoke step
                  const input = bespokeStep.querySelector('input');
                  if (input) {
                      input.value = ''; // Clear input value
                  }
                  if(gateHeightSelected) {
                    if(gateHeightSelected.includes('cm')) {
                      var heightNoCM = parseFloat(gateHeightSelected.replace('cm', ''));
                      var cmtoMM = heightNoCM * 10;

                      bespokeProperty.value = cmtoMM;
                    }
                  }
              }
        
              setTimeout(updatePedestrianGateBespokePricing, 500);
          }
          
          function handleDecorativePanelBespokeVariant(variant) {
            const bespokeStep = document.querySelectorAll('.bespoke-input');
            const bespokeInputEntry = document.querySelectorAll('.bespoke-input-entry');

            const bespokeHeightProperty = document.querySelector('.bespoke-height-property');
            const bespokeWidthProperty = document.querySelector('.bespoke-width-property');

            const productQuantity = document.querySelector('.product__block--buy_buttons .product-form__quantity');
        
            const variantTitle = variant.title.toLowerCase(); // Convert title to lowercase
        
            if (variantTitle.includes('bespoke size')) {
                bespokeStep.forEach(step => {
                  step.classList.remove('hide-step');
                });

                if(productQuantity) {
                  productQuantity.classList.add('hidden');
                }
            } else {
                bespokeStep.forEach(step => {
                  step.classList.add('hide-step');
                });

                bespokeInputEntry.forEach(input => {
                  if (input) {
                      input.value = ''; // Clear input value
                  }
                });

                if (bespokeHeightProperty) {
                  bespokeHeightProperty.value = '';
                }

                if (bespokeWidthProperty) {
                  bespokeWidthProperty.value = '';
                }

                if(productQuantity) {
                  productQuantity.classList.remove('hidden');
                }
            }
      
            setTimeout(updateDecorativePanelBespokePricing, 500);
          }

          function handleInsuslatBespokeVariant(variant) {
            const bespokeStep = document.querySelectorAll('.bespoke-input');
            const bespokeInputEntry = document.querySelectorAll('.bespoke-input-entry');

            const bespokeHeightProperty = document.querySelector('.bespoke-height-property');
            const requiredMessage = document.querySelector('.required-message');
        
            const variantTitle = variant.title.toLowerCase(); // Convert title to lowercase
        
            if (variantTitle.includes('yes')) {
                bespokeStep.forEach(step => {
                  step.classList.remove('hide-step');
                });
            } else {
                bespokeStep.forEach(step => {
                  step.classList.add('hide-step');
                });

                bespokeInputEntry.forEach(input => {
                  if (input) {
                    input.value = ''; // Clear input value
                  }
                });

                bespokeHeightProperty.value = '';
                requiredMessage.classList.remove('active');
            }
          }

          function handleAluminiumBespokeRalVariant(variant) {
            const bespokeRalStep = document.querySelector('.bespoke-ral-code-input-block');
            const bespokeRalProperty = document.querySelector('.bespoke-ral-code-property');

            if(bespokeRalStep) {
              const bespokeRalInput = document.querySelector('.custom-ral-code-input');
              const requiredMessage = document.querySelector('.required-message');
          
              const variantTitle = variant.title.toLowerCase(); // Convert title to lowercase
          
              if (variantTitle.includes('bespoke ral colour')) {
                bespokeRalStep.classList.remove('hide-step');
              } else {
                bespokeRalStep.classList.add('hide-step');

                bespokeRalInput.value = ''; // Clear input value

                bespokeRalProperty.value = '';
                requiredMessage.classList.remove('active');
              }
            }
          }

          function updateRALPricing(variant) {
            const bespokeRalStep = document.querySelector('.bespoke-ral-code-input-block');

            if(bespokeRalStep) {
              var variantPrice = variant.price;
              var ralProduct = document.querySelector('.bespoke-ral-product');
              if(ralProduct){
                var ralUpdatedQuantity = Math.round(variantPrice * 0.1);
                ralProduct.setAttribute('data-quantity', ralUpdatedQuantity);
              }
            }

            buildTotalCost();
          }

          // Pedestrian Gates Functions
          if (document.querySelector('body').classList.contains('template-pedestrian-gates')) {
            handleBespokeVariant(variant);
            updateSlatColourProperty();
            handleAluminiumBespokeRalVariant(variant);
            gateRalCode();
            pedestrianGateAdditionalPostSelection();
          }

          // Driveway Gates Functions
          if (document.querySelector('body').classList.contains('template-Gates')) {
            updateSlatColourProperty();
            handleAluminiumBespokeRalVariant(variant);
            gateRalCode();
          }

          // Decorative Panels Functions
          if (document.querySelector('body').classList.contains('template-decorative-panels')) {
            handleDecorativePanelBespokeVariant(variant);
          }

          // InsuSlat Functions
          if (document.querySelector('body').classList.contains('template-wall_panel')) {
            handleInsuslatBespokeVariant(variant);
          }

          // Fence Functions
          if (document.querySelector('body').classList.contains('template-kit')) {
            updateRALPricing(variant);
          }

          const stepCount = document.querySelectorAll('.product-step .count');
          stepCount.forEach((count) => {
            count.classList.add('hidden');
          });

          setTimeout(() => {
            updateStepCounts();
            stepTooltip();
          }, 500);

          document.dispatchEvent(
            new CustomEvent('variant:changed', {
              detail: {
                variant: variant,
              },
            })
          );
        };
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}?${params.join('&')}`;
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
          })
          .then(() => {
            // set focus to last clicked option value
            //document.querySelector(`#${targetId}`)?.focus();

            // Fence Functions
            if (document.querySelector('body').classList.contains('template-kit')) {
              runFenceMeasurementScript(false);
              updateFenceHeightValues();
              initializeInstallationSelection(false);
            }

            // Driveway Gates Functions
            if (document.querySelector('body').classList.contains('template-Gates')) {
              checkAutomationKitSelection();
              drivewayGatesEntryChecks();
              updateDrivewayGateBespokePricing();

              const bespokeHeightInput = document.querySelector('.gate_bespoke_height').value;
              aluminiumPostSelection(bespokeHeightInput);
            }

            // Pedestrian Gates Functions
            if (document.querySelector('body').classList.contains('template-pedestrian-gates')) {
              pedestrianGatesEntryChecks();
            }

            // Planters Functions
            if (document.querySelector('body').classList.contains('template-planters')) {
              planterWheelSelection();
            }

            // Decorative Panels Functions
            if (document.querySelector('body').classList.contains('template-decorative-panels')) {
              decorativePanelsEntryChecks();
              decorativePanelProperties();
            }

            // Decorative Panels Functions
            if (document.querySelector('body').classList.contains('template-wall_panel')) {
              insuslatEntryChecks();
            }

            // Mirrors Functions
            if (document.querySelector('body').classList.contains('template-mirrors')) {
              mirrorLightingSelection();
              //mirrorUpdateATCButton();

              if(!window.location.href.includes('with-led') && document.querySelector('input#yes-mirror-lighting:checked')) {
                mirrorLEDImageSelection();
              }
            }

            buildTotalCost();

            setTimeout(() => {
              stepTooltip();
            }, 500);

          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              //console.log('Fetch aborted by user');
            } else {
              console.error(error);
            }
          });
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        }
      }

      updateURL(url, variantId) {
        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      updateVariantInputs(variantId) {
        document
          .querySelectorAll(`#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`)
          .forEach((productForm) => {
            const input = productForm.querySelector('input[name="id"]');
            input.value = variantId ?? '';
            input.dispatchEvent(new Event('change', { bubbles: true }));
          });
      }

      updateMedia(variant) {
        const productMedia = this.querySelector(`[id^="MediaGallery-${this.dataset.section}"]`);
        if (!productMedia) return; // Early return if productMedia is not found

        const setActiveMedia = () => {
          if (typeof productMedia.setActiveMedia === 'function') {
            productMedia.init();
            productMedia.setActiveMedia(variant);
            return true; // Indicate success
          }
          return false; // Indicate failure
        };

        if (!setActiveMedia()) {
          this.timer = setInterval(() => {
            if (setActiveMedia()) {
              clearInterval(this.timer);
            }
          }, 100);
        }
      }

      updateShareUrl(variantId) {
        if (!variantId) return;
        const shareButton = document.getElementById(`ProductShare-${this.dataset.section}`);
        if (!shareButton || !shareButton.updateUrl) return;
        shareButton.updateUrl(`${window.shopUrl}${this.dataset.url}?variant=${variantId}`);
      }

      toggleAddButton(disable = true, text, modifyClass = true) {
        const productForm = document.getElementById(`product-form-${this.dataset.section}`);
        if (!productForm) return;
        const addButton = productForm.querySelector('[name="add"]');
        const addButtonText = productForm.querySelector('[name="add"] > span');
        if (!addButton) return;

        if (disable) {
          addButton.setAttribute('disabled', 'disabled');
          if (text) addButtonText.textContent = text;
        } else {
          addButton.removeAttribute('disabled');
          addButton.classList.remove('btn--disabled');
          addButtonText.textContent = FoxTheme.variantStrings.addToCart;
        }

        if (!modifyClass) return;
      }

      setUnavailable() {
        const button = document.getElementById(`product-form-${this.dataset.section}`);
        const addButton = button.querySelector('[name="add"]');
        const addButtonText = button.querySelector('[name="add"] > span');
        const price = document.getElementById(`price-${this.dataset.section}`);
        const inventory = document.getElementById(`Inventory-${this.dataset.section}`);
        const sku = document.getElementById(`Sku-${this.dataset.section}`);

        if (!addButton) return;
        addButtonText.textContent = FoxTheme.variantStrings.unavailable;
        addButton.classList.add('btn--disabled');
        if (price) price.classList.add('hidden');
        if (inventory) inventory.classList.add('hidden');
        if (sku) sku.classList.add('hidden');
      }

      setAvailable() {
        const button = document.getElementById(`product-form-${this.dataset.section}`);
        const addButton = button.querySelector('[name="add"]');

        if (!addButton) return;
        addButton.classList.remove('btn--disabled');
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.setQuantityBoundries();
        if (!this.hasAttribute('data-original-section')) {
          this.cartUpdateUnsubscriber = FoxTheme.pubsub.subscribe(
            FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate,
            this.fetchQuantityRules.bind(this)
          );
        }
      }

      setQuantityBoundries() {
        FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.quantityBoundries, {
          data: {
            sectionId: this.sectionId,
            productId: this.productId,
          },
        });
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.productIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart')?.classList.add('btn--loading');

        fetch(`${this.getAttribute('data-url')}?variant=${currentVariantId}&section_id=${this.sectionId}`)
          .then((response) => response.text())
          .then((responseText) => {
            const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.sectionId, this.productId, parsedHTML);
          })
          .catch((error) => {
            console.error(error);
          })
          .finally(() => {
            this.querySelector('.quantity__rules-cart')?.classList.remove('btn--loading');
          });
      }

      updateQuantityRules(sectionId, productId, parsedHTML) {
        if (!this.quantityInput) return;

        FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.quantityRules, {
          data: {
            sectionId,
            productId,
            parsedHTML,
          },
        });

        this.setQuantityBoundries();
      }
    }
  );
}


//
//  LM: Custom Functions
//

// LM: Quick Links
document.addEventListener("DOMContentLoaded", function () {
  const buttons = [
    { selector: ".video-quick-link", target: ".video-with-text" },
    { selector: ".faq-quick-link", target: ".faq-column" },
    { selector: ".specification-quick-link", target: ".spec-column" },
    { selector: ".benefits-quick-link", target: ".product-features" },
  ];

  buttons.forEach(({ selector, target }) => {
    const button = document.querySelector(selector);
    const section = document.querySelector(target);

    if (button && section) {
      button.addEventListener("click", function () {
        const headerOffset = 145;
        const sectionTop = section.getBoundingClientRect().top + window.scrollY;
        window.scrollTo({
          top: sectionTop - headerOffset - 30,
          behavior: "smooth",
        });
      });
    }
  });
});

// LM: Product Step Tooltips
function stepTooltip() {
  document.querySelectorAll('.product-step').forEach(step => {
    const infoButton = step.querySelector('.info');
    const tooltip = step.querySelector('.step-info-tooltip');

    if (infoButton && tooltip) {
      infoButton.addEventListener('click', (event) => {
          // Close all tooltips first
          document.querySelectorAll('.step-info-tooltip.active').forEach(activeTooltip => {
              if (activeTooltip !== tooltip) {
                  activeTooltip.classList.remove('active');
              }
          });

          // Toggle the clicked tooltip
          tooltip.classList.toggle('active');

          // Prevent event bubbling if needed
          event.stopPropagation();
      });
    }
  });
}

function calculateViewAllContainerHeight() {
  const viewAllSteps = document.querySelectorAll('.view-all-step');
  viewAllSteps.forEach(step => {
    const stepContent = step.querySelector('.step-content.short-content');
    const viewAllWrapper = step.querySelector('.view-all-wrapper');

    // Calculate container height to show 3 rows + 1/3 of next item
    function calculateContainerHeight() {
      if(!stepContent) return;
      if (stepContent.classList.contains('short-content')) {
        var rows = [...stepContent.querySelectorAll('.step-item')];
        if (rows.length >= 4) {
          const firstThreeHeight = rows.slice(0, 3).reduce((total, el) => total + el.offsetHeight, 0);
          const halfHeight = rows[3].offsetHeight / 2;
          const rowGap = stepContent.classList.contains('aluminium-slat') ? 58 : stepContent.classList.contains('insuslat-colour-step-content') ? 86 : 30;
          stepContent.style.maxHeight = (firstThreeHeight + halfHeight + rowGap) + 'px';
        }
      }

      if(stepContent.classList.contains('two-step-mirror-content')) {
        if(!rows) return;
        if (rows.length <= 6) {
          stepContent.classList.remove('short-content');
          viewAllWrapper.classList.add('!hidden');
        }
      }
    }
    
    // Calculate height on load and resize
    calculateContainerHeight();
    window.addEventListener('resize', calculateContainerHeight);
  });
}

// LM: View All Logic
function viewAllLogic() {
  const viewAllSteps = document.querySelectorAll('.view-all-step');
  viewAllSteps.forEach(step => {
    const viewAllWrapper = step.querySelector('.view-all-wrapper');
    const viewAllButton = step.querySelector('.view-all-logic-button');
    const stepContent = step.querySelector('.step-content.short-content');

    viewAllButton.addEventListener('click', () => {
      viewAllWrapper.classList.add('!hidden');
      stepContent.classList.remove('short-content');
      stepContent.style.maxHeight = 'none';
    });
  });
}

//
//  LM: Product Step Count
//
function updateStepCounts() {
  let count = 1; // Reset count on each run

  // Select all '.product-step' elements in order
  const elements = document.querySelectorAll('.product-step');

  elements.forEach((element) => {
    if (!element.classList.contains('hide-step')) { // Ensure only visible steps are counted
        const countElement = element.querySelector('.count');
        if (countElement) {
            countElement.textContent = count;
            countElement.classList.remove('hidden'); // Assign new count
        }
        count++; // Increment count
    }
  });
}

//
//  LM: Sticky ATC - Either trigger main cart or scroll to it if disabled
//
function triggerMainAddToCart() {
  const atcButton = document.querySelector('.btn--atc-scroll');
  const mainAddToCartButton = document.querySelector('.product__block--buy_buttons .btn--primary');

  if (!atcButton || !mainAddToCartButton) return; // Ensure elements exist

  atcButton.addEventListener('click', (e) => {
      e.preventDefault();

      if (!mainAddToCartButton.getAttribute('disabled')) {
        mainAddToCartButton.click();
        atcButton.classList.add('btn--loading');

        setTimeout(() => {
          atcButton.classList.remove('btn--loading');
        }, 2000);
      } else {
        const headerHeight = window.innerWidth <= 1024 ? 160 : 160; // 72px for mobile, 100px for desktop
        const targetPosition = mainAddToCartButton.getBoundingClientRect().top + window.scrollY - headerHeight;

        window.scrollTo({
            top: targetPosition,
            behavior: 'smooth'
        });
      }
  });
}

// LM: Slat Type Selection
function updateSlatType() {
  const slatTypeOptions = document.querySelectorAll('input[name="slat-type"]');
  const slatTypeContent = document.querySelectorAll('.slat-type-content .slat-step');
  const viewAllWrapper = document.querySelector('.slat-type-content .view-all-wrapper');
  slatTypeOptions.forEach(option => {
    option.addEventListener('change', () => {
      const slatType = option.value;

      slatTypeContent.forEach(content => {
        if (content.classList.contains(slatType)) {
          content.classList.remove('hide');
        } else {
          content.classList.add('hide');
        }
      });

      if(slatType === 'aluminium-slat') {
        calculateViewAllContainerHeight();

        if(viewAllWrapper) {
          viewAllWrapper.classList.remove('comp-active');
        }
      } else {
        if(viewAllWrapper) {
          viewAllWrapper.classList.add('comp-active');
        }
      }
    });
  });
}

function gateCustomWording() {
  const customWordingInput = document.querySelector('.custom-wording-input');
  const customWordingProperty = document.querySelector('.custom-wording-property');

  if(customWordingInput) {
    customWordingInput.addEventListener('input', () => {
      customWordingProperty.value = customWordingInput.value;
    });
  }
}

function gateRalCode() {
  const bespokeRalInput = document.querySelector('.custom-ral-code-input');
  const bespokeRalProperty = document.querySelector('.bespoke-ral-code-property');

  if(bespokeRalInput) {
    bespokeRalInput.addEventListener('input', () => {
      bespokeRalProperty.value = bespokeRalInput.value;
    });
  }
}

function storeStepItemData() {
  // Get data-template from body
  const body = document.body;
  const dataTemplate = body.getAttribute('data-template') || '';
  
  // Find all .step-item elements that are anchor tags
  const stepItems = document.querySelectorAll('.step-item');
  
  stepItems.forEach((stepItem) => {
    // Check if it's an anchor element
    if (stepItem.tagName === 'A') {
      // Add click event listener to each anchor
      stepItem.addEventListener('click', function(event) {
        // Find the closest parent with class 'product-step'
        const productStep = this.closest('.product-step');
        
        if (productStep) {
          // Get the data-step attribute from the parent
          const dataStep = productStep.getAttribute('data-step') || '';
          
          // Store the data for this clicked item
          const stepItemData = {
            step: dataStep,
            template: dataTemplate
          };
          
          // Store in session storage
          sessionStorage.setItem('stepItemData', JSON.stringify(stepItemData));
        }
      });
    }
  });
}

function moreToStep() {
  const stepItemData = sessionStorage.getItem('stepItemData');
  if(stepItemData) {
    const stepItemDataParsed = JSON.parse(stepItemData);
    const step = stepItemDataParsed.step;
    const template = stepItemDataParsed.template;

    var templateName = document.querySelector('body').getAttribute('data-template');
    var stepItem = document.querySelector('.product-step[data-step="' + step + '"]');
    if(!stepItem) return;
    var mobileCalc = stepItem.querySelector('.step-header').getAttribute('data-media-wrapper-height');
    if(templateName === template) {
      if(stepItem) {
        const elementTop = stepItem.getBoundingClientRect().top + window.pageYOffset;
        window.scrollTo({ top: elementTop, behavior: 'smooth' });
        if (window.innerWidth <= 768) {
          window.scrollTo({ top: elementTop - mobileCalc, behavior: 'smooth' });
        } else {
          window.scrollTo({ top: elementTop, behavior: 'smooth' });
        }
      }
    }
  }
}


//
//  LM: Fence Functions
//

// LM: Fencing Products - Variant Measurmement Script
function runFenceMeasurementScript(initialLoad) {
  if (initialLoad) {
    sessionStorage.removeItem("measurementState");
  }

  document.querySelectorAll(".measurement-content").forEach((variantContent) => {
    const cmButton = variantContent.querySelector(".button-cm");
    const ftButton = variantContent.querySelector(".button-ft");

    // Function to update classes and active state
    function updateMeasurementClass(toCM) {
      variantContent.classList.toggle("cm", toCM);
      variantContent.classList.toggle("ft", !toCM);
      cmButton.classList.toggle("active", toCM);
      ftButton.classList.toggle("active", !toCM);

      // Save the selected state in sessionStorage
      sessionStorage.setItem("measurementState", toCM ? "cm" : "ft");
    }

    // Event listeners for button clicks
    cmButton.addEventListener("click", () => updateMeasurementClass(true));
    ftButton.addEventListener("click", () => updateMeasurementClass(false));

    // Set default state based on sessionStorage
    const savedState = sessionStorage.getItem("measurementState");

    if (savedState === "ft") {
      // Set to 'ft' if saved in sessionStorage
      updateMeasurementClass(false);
    } else {
      // Default to 'cm' if no saved state
      updateMeasurementClass(true);
    }
  });
}

// LM: Fencing Products - Update Height Values
function updateFenceHeightValues() {
  const checkedInput = document.querySelector(".measurement-content input[type='radio']:checked");
  const product_title = document.querySelector("h1").textContent;
  if (checkedInput) {
      let value = checkedInput.value.replace(" BG", ""); // Remove ' BG' if present

      // Update elements if they exist
      document.querySelectorAll(".item-height, .item-post-height").forEach(element => {
          element.querySelector('.metafield-rich_text_field').textContent = value;
      });

      if(product_title.includes('Virtis')) {
        let valueStripped = value.replace('cm', '');
        let valueNumber = parseInt(valueStripped);
        let newValue = valueNumber - 10;
        let newValuewithCM = String(newValue) + 'cm';
        // Update elements if they exist
        document.querySelectorAll(".item-slat-height").forEach(element => {
            element.querySelector('.metafield-rich_text_field').textContent = newValuewithCM;
        });
      }
  }
}

// LM: Fencing Products - Installation Method Selection
function initializeInstallationSelection(initialLoad) {
  const installationRadios = document.querySelectorAll('input[name="installation-method"]');
  const stepContent = document.querySelector(".step-content.measurement-content");

  if (initialLoad) {
    sessionStorage.removeItem("installation-method");
  }

  if (!installationRadios.length || !stepContent) return;

  // Restore previous selection from sessionStorage
  const storedInstallation = sessionStorage.getItem("installation-method");
  if (storedInstallation) {
      const radioToCheck = [...installationRadios].find(radio => radio.value.toLowerCase() === storedInstallation.toLowerCase());
      if (radioToCheck) {
          radioToCheck.checked = true;
          stepContent.classList.toggle("below-ground", storedInstallation.toLowerCase() === "sunk into ground");

          // Prevent infinite loop by ensuring we only trigger click once
          if (!radioToCheck.dataset.clickTriggered) {
              radioToCheck.dataset.clickTriggered = "true"; // Set flag
              setTimeout(() => {
                  radioToCheck.dispatchEvent(new Event("click", { bubbles: true }));
                  radioToCheck.removeAttribute("data-click-triggered"); // Reset flag after event
              }, 0);
          }
      }
  }

  installationRadios.forEach(radio => {
      radio.addEventListener("change", function () {
          sessionStorage.setItem("installation-method", this.value); // Save selection
          if (this.value.toLowerCase() === "sunk into ground") {
              stepContent.classList.add("below-ground");
          } else {
              stepContent.classList.remove("below-ground");
          }
          syncPostHeightSelection();
      });
  });

  const postHeightRadios = document.querySelectorAll('.measurement-content input');

  function syncPostHeightSelection() {
      const selected = document.querySelector('.measurement-content input:checked');
      if (!selected) return;

      const selectedValue = selected.value.replace(/ bg$/i, ""); // Normalize value, case insensitive
      const isBelowGround = stepContent.classList.contains("below-ground");

      postHeightRadios.forEach(radio => {
          let targetValue = isBelowGround ? (selectedValue + " BG").toLowerCase() : selectedValue.toLowerCase();

          // Only change selection if it's not already selected
          if (radio.value.toLowerCase() === targetValue && !radio.checked) {
              radio.checked = true;

              // Prevent infinite loop when triggering change event
              if (!radio.dataset.changeTriggered) {
                  radio.dataset.changeTriggered = "true";
                  setTimeout(() => {
                      radio.dispatchEvent(new Event("change", { bubbles: true }));
                      radio.removeAttribute("data-change-triggered");
                  }, 0);
              }
          }
      });
  }

  postHeightRadios.forEach(radio => {
      radio.addEventListener("change", function () {
          // Prevent recursive loops
          setTimeout(syncPostHeightSelection, 0);
      });
  });
}

// LM: Fencing Products - Toggle Lumifence Lighting Option, with Total Cost update when triggered
function toggleLightingProduct() {
  const lightingOptions = document.querySelectorAll(".step-content.lighting-step input");
  const lightingPowerBlock = document.querySelector(".lighting-power-block");
  const extraPostWallLightStep = document.querySelector(".extra-post-wall-light-step");
  const extraPostWallLightStepInputs = document.querySelectorAll(".extra-post-wall-light-step input");

  lightingOptions.forEach(option => {
    option.addEventListener("change", function (event) {
      const selectedLightingOption = document.querySelector('.light-design-option:checked');

      if(selectedLightingOption) {
        const postLightType = selectedLightingOption.getAttribute('data-type');
        const activeWrapper = document.querySelector(`.post-light-wrapper[data-design="${postLightType}"]`);
        if (activeWrapper) {
            activeWrapper.classList.add('active');
        }
      }

      lightingPowerBlock.classList.add("hide-step");
      lightingPowerBlock.classList.remove("active");
      extraPostWallLightStep.classList.add("hide-step");
      extraPostWallLightStep.classList.remove("active");

      if (event.target.value.includes("LumiFence")) {
        lightingPowerBlock.classList.add("active");
        lightingPowerBlock.classList.remove("hide-step");
      } else if (event.target.value.includes("Post")) {
        extraPostWallLightStep.classList.add("active");
        extraPostWallLightStep.classList.remove("hide-step");
      }

      updateStepCounts();
      buildTotalCost();
    });
  });

  extraPostWallLightStepInputs.forEach(input => {
    input.addEventListener("change", function () {
      buildTotalCost();
    });
  });
}

// LM: Fencing Products - Lighting Product has unique rules around quantity and power selection.
function updatePowerProduct() {
  var lumifenceSelected = document.querySelector(".lighting-power-block");
  if(lumifenceSelected && lumifenceSelected.classList.contains('active')) {
    var quantityInput = document.querySelector('.product__block--buy_buttons .quantity__input'); // Get the quantity input value
    var quantity = parseInt(quantityInput.value);
    var powerSelect = document.getElementById('power-options-select');
    var powerSelected = document.querySelector(".lighting-power-content input[type='radio']:checked");
  
    // Get the current selected value (either 'Mains Powered' or 'Solar Powered')
    var selectedValue = powerSelected.value;
  
    // Loop through the options in the select menu
    for (var i = 0; i < powerSelect.options.length; i++) {
      var option = powerSelect.options[i];
  
      // Create the exact match string we are looking for
      var exactMatch = `${selectedValue} / ${quantity}`;
  
      // Check if the option matches the exact string format (e.g., 'Mains Powered / 1')
      if (option.textContent.trim() === exactMatch) {
        // If the option matches, select it
        option.selected = true;
      }
    }
  }
}

// LM: Fencing Products - Panel Trim Options
function fencePanelTrimOptions() {
  var panelTrimOptionStep = document.querySelector(".panel-trim-option-step");

  if(panelTrimOptionStep) {
    const panelColourBlock = document.querySelector(".panel-colour-block");
    const panelTrimOptions = document.querySelectorAll(".panel-trim-option-step input");
    const lumifenceLightingInput = document.querySelector('input#lumifence');
    const lumifenceLightingLabel = document.querySelector('label[for="lumifence"]');
    const noLightingOption = document.querySelector('label[for="no-lighting"]');

    panelTrimOptions.forEach(option => {
      option.addEventListener("change", function () {
        if(option.value === 'Upgraded Trim') {
          panelColourBlock.classList.remove("hide-step");

          lumifenceLightingLabel.classList.add("!hidden");

          if(lumifenceLightingInput.checked) {
            noLightingOption.click();
          }
        } else {
          panelColourBlock.classList.add("hide-step");

          lumifenceLightingLabel.classList.remove("!hidden");
        }

        updateStepCounts();
        buildTotalCost();
      });
    });
  }
}

// LM: Fencing Products - Slat Planter Options
function fenceSlatPlanterOptions() {
  var slatPlanterOptionStep = document.querySelector(".slat-planter-option-step");

  if(slatPlanterOptionStep) {
    const slatPlanterOptions = document.querySelectorAll(".slat-planter-option-step .slat-planter-wrapper input");
    const slatPlanterBlock = document.querySelector(".slat-planter-block");
    const slatPlanterColourOptions = document.querySelectorAll(".slat-planter-colour-block .content input");
    const slatPlanterQuantityOptions = document.querySelectorAll(".slat-planter-quantity-block .product-form__quantity .quantity__input");

    slatPlanterOptions.forEach(option => {
      option.addEventListener("change", function () {
        if(option.value === 'Yes Slat Planter') {
          slatPlanterBlock.classList.remove("hide-step");
        } else {
          slatPlanterBlock.classList.add("hide-step");
        }

        updateStepCounts();
        buildTotalCost();
      });
    });

    slatPlanterColourOptions.forEach(option => {
      option.addEventListener("change", function () {
        buildTotalCost();
      });
    });

    slatPlanterQuantityOptions.forEach(option => {
      option.addEventListener("change", function () {
        buildTotalCost();
      });
    });
  }
}

function RALFence() {
  const bespokeRalInput = document.querySelector('.custom-ral-code-input');
  const bespokeRalProperty = document.querySelector('.bespoke-ral-code-property');

  if(bespokeRalInput) {
    const atcButton = document.querySelector(".product-form__submit");
    const requiredMessage = document.querySelector('.required-message');

    bespokeRalInput.addEventListener('input', () => {
      bespokeRalProperty.value = bespokeRalInput.value;
    });

    function updateATCButton() {

      if (bespokeRalInput) {
        var bespokeRalFilled = bespokeRalInput.value.trim() !== "";
      }

      if (bespokeRalFilled) {
        atcButton.removeAttribute("disabled");
        requiredMessage.classList.remove('active');
      } else {
        atcButton.setAttribute("disabled", "true");
        requiredMessage.classList.add('active');
      }
    }

    bespokeRalInput.addEventListener('input', updateATCButton);
    updateATCButton();
  }
}

//
//  LM: Bespoke Entry Function
//

function enforceBespokeInputRange() {
  // Select all '.bespoke-input' elements
  const bespokeInputs = document.querySelectorAll('.bespoke-input input');
  
  bespokeInputs.forEach(input => {
      // Set the min and max from the input attributes
      const min = parseFloat(input.getAttribute('min'));
      const max = parseFloat(input.getAttribute('max'));

      // Add blur event listener
      input.addEventListener('blur', () => {
          let value = parseFloat(input.value);

          // Ensure the value is within the min and max range
          if (value < min) {
              input.value = min; // If below min, set to min
              value = min;
          } else if (value > max) {
              input.value = max; // If above max, set to max
              value = max;
          }

          if(input.classList.contains('gate_bespoke_height')) {
            var bespokeHeightProperty = document.querySelector('.bespoke-height-property');
            bespokeHeightProperty.value = value;

            aluminiumPostSelection(value);
            
          } else if (input.classList.contains('gate_bespoke_width')) {
            var bespokeWidthProperty = document.querySelector('.bespoke-width-property');
            bespokeWidthProperty.value = value;
          } else if (input.classList.contains('panel_bespoke_height')) {
            var bespokeWidthProperty = document.querySelector('.bespoke-height-property');
            bespokeWidthProperty.value = value;
          } else if (input.classList.contains('panel_bespoke_width')) {
            var bespokeWidthProperty = document.querySelector('.bespoke-width-property');
            bespokeWidthProperty.value = value;
          } else if (input.classList.contains('insuslat-bespoke-height')) {
            var bespokeWidthProperty = document.querySelector('.bespoke-height-property');
            bespokeWidthProperty.value = value;
          }
      });
  });
}

//
//  LM: Pedestrian Gate Functions
//

function updatePedestrianGateBespokePricing() {
  const bespokeSizeInput = document.querySelector('input[value="BESPOKE SIZE"]');
  const bespokeHeightInput = document.querySelector('.gate_bespoke_height');
  const bespokeWidthInput = document.querySelector('.gate_bespoke_width');
  const bespokeGateMinPricing = document.querySelector('.bespoke-gate-min-pricing');
  const bespokeGatePricing = document.querySelector('.bespoke-gate-pricing');
  const moneyFormat = FoxTheme.settings.moneyFormat;
  const mainProductPriceContainer = document.querySelector('.product__block--price .f-price');
  const mainProductPrice = document.querySelectorAll('.product__block--price .f-price-item');
  const mainProductComparePrice = document.querySelector('.product__block--price .f-price__sale .compare-price');
  const bespokeGateProduct = document.querySelector('.bespoke-product');
  const bespokeGateProductQuantity = parseFloat(bespokeGateProduct.getAttribute('data-quantity'));

  const bespokeHeightInputMin = parseFloat(bespokeHeightInput.getAttribute('min'));
  const bespokeHeightInputMax = parseFloat(bespokeHeightInput.getAttribute('max'));
  const bespokeWidthInputMin = parseFloat(bespokeWidthInput.getAttribute('min'));
  const bespokeWidthInputMax = parseFloat(bespokeWidthInput.getAttribute('max'));
  
  let bespokePrice = 0;
  let bespokeComparePrice = 0;

  // Example Calculation:
  // Height: 180cm × Width: 90cm
  // Total Area: 180 × 90 = 16,200 cm²
  // Gate Price: 16,200 × £0.057 = £923.40

  // If "BESPOKE SIZE" is selected
  if (bespokeSizeInput && bespokeSizeInput.checked) {
    // If the bespoke height & width is populated
    if (bespokeHeightInput.value && bespokeWidthInput.value) {
      var bespokeHeight = parseFloat(bespokeHeightInput.value);
      var bespokeWidth = parseFloat(bespokeWidthInput.value);
      const dataPrice = parseFloat(bespokeGatePricing.getAttribute('data-price'));
      const dataComparePrice = parseFloat(bespokeGatePricing.getAttribute('data-compare-price'));
      const bespokeMinPricing = parseFloat(bespokeGateMinPricing.getAttribute('data-price'));

      if(bespokeHeight < bespokeHeightInputMin) {
        bespokeHeight = bespokeHeightInputMin;
      } else if (bespokeHeight > bespokeHeightInputMax) {
        bespokeHeight = bespokeHeightInputMax;
      }

      if(bespokeWidth < bespokeWidthInputMin) {
        bespokeWidth = bespokeWidthInputMin;
      } else if (bespokeWidth > bespokeWidthInputMax) {
        bespokeWidth = bespokeWidthInputMax;
      }
      
      // Get the total area in cm², height and width are in mm so we divide by 100
      let total_area = bespokeHeight * bespokeWidth;
      total_area = Math.round(total_area / 100);
      total_area_quantity = Math.round(total_area / 10);

      // Adjust prices to reflect the price per cm² (divide by 10)
      const adjustedDataPrice = dataPrice / 10;
      const adjustedDataComparePrice = dataComparePrice / 10;

      // Calculate the bespoke pricing based on total area (price per cm²)
      const bespokePriceCalc = adjustedDataPrice * total_area;

      // Adjust the compare price similarly
      const bespokeComparePriceCalc = adjustedDataComparePrice * total_area;

      // If the bespoke pricing is more than the minimum pricing, use the new custom amounts otherwise fall back to minimum pricing.
      if(bespokePriceCalc > bespokeMinPricing) {
        if(bespokeGateProduct.classList.contains('new-aluminium')) {
          bespokePrice = bespokePriceCalc;
          bespokeComparePrice = bespokeComparePriceCalc;

          var price_difference = bespokePriceCalc - bespokeMinPricing;
              adjustedQuantity = price_difference / dataPrice;

          bespokeGateProduct.setAttribute('data-quantity', Math.round(adjustedQuantity));
        } else {
          bespokePrice = bespokePriceCalc;
          bespokeComparePrice = bespokeComparePriceCalc;
          bespokeGateProduct.setAttribute('data-quantity', total_area_quantity);
        }
      } else {
        // If the bespoke height is NOT populated, use the minimum pricing
        bespokePrice = parseFloat(bespokeGateMinPricing.getAttribute('data-price'));
        bespokeComparePrice = parseFloat(bespokeGateMinPricing.getAttribute('data-compare-price'));
        bespokeGateProduct.setAttribute('data-quantity', bespokeGateProductQuantity);
      }

      var initialFrom = document.querySelectorAll('.initial-from');
      initialFrom.forEach(item => {
        item.classList.add('!hidden');
      });

    } else {
      // If the bespoke height is NOT populated, use the minimum pricing
      bespokePrice = parseFloat(bespokeGateMinPricing.getAttribute('data-price'));
      bespokeComparePrice = parseFloat(bespokeGateMinPricing.getAttribute('data-compare-price'));
      bespokeGateProduct.setAttribute('data-quantity', bespokeGateProductQuantity);
    }

    mainProductPriceContainer.classList.add('f-price--on-sale');

    const formattedPrice = FoxTheme.Currency.formatMoney(bespokePrice, moneyFormat);
    const formattedComparePrice = FoxTheme.Currency.formatMoney(bespokeComparePrice, moneyFormat);

    mainProductPrice.forEach(price => {
      price.textContent = formattedPrice;
      price.setAttribute('data-price', bespokePrice);
    });

    mainProductComparePrice.textContent = formattedComparePrice;
    mainProductComparePrice.setAttribute('data-compare', bespokeComparePrice);

    var ralProduct = document.querySelector('.bespoke-ral-product');
    if(ralProduct){
      var ralUpdatedQuantity = Math.round(bespokePrice * 0.12);
      ralProduct.setAttribute('data-quantity', ralUpdatedQuantity);
    }

    buildTotalCost(bespokePrice, bespokeComparePrice)
  }
}

function pedestrianGatesEntryChecks() {
  const bespokeWidthInput = document.querySelector(".gate_bespoke_width");
  const variantSelect = document.querySelector("input[value='BESPOKE SIZE']");
  const bespokeHeightInput = document.querySelector(".gate_bespoke_height");
  const atcButton = document.querySelector(".product-form__submit");
  const requiredMessage = document.querySelector('.required-message');
  const customWordingInput = document.querySelector('.custom-wording-input');
  const bespokeRalStep = document.querySelector('.bespoke-ral-code-input-block');
  const bespokeRalInput = document.querySelector('.custom-ral-code-input');

  function updateATCButton() {
    if (bespokeWidthInput) {
      var widthFilled = bespokeWidthInput.value.trim() !== "";
    }

    if (customWordingInput) {
      var customWordingFilled = customWordingInput.value.trim() !== "";
    }

    if (bespokeRalInput) {
      var bespokeRalFilled = bespokeRalInput.value.trim() !== "";
    }

    const heightRequired = variantSelect && variantSelect.checked;

    if (bespokeHeightInput) {
      var heightFilled = bespokeHeightInput.value.trim() !== "";
    }

    if(customWordingInput) {
      const requiredFields = [
        !heightRequired || heightFilled,
        widthFilled,
        customWordingFilled,
        bespokeRalStep ? (!bespokeRalStep.classList.contains('hide-step') ? bespokeRalFilled : true) : true
      ];

      const allFieldsFilled = requiredFields.every(field => field);

      if (allFieldsFilled) {
        atcButton.removeAttribute("disabled");
        requiredMessage.classList.remove('active');
      } else {
        atcButton.setAttribute("disabled", "true");
        requiredMessage.classList.add('active');
      }
    } else {
      const requiredFields = [
        !heightRequired || heightFilled,
        widthFilled,
        bespokeRalStep ? (!bespokeRalStep.classList.contains('hide-step') ? bespokeRalFilled : true) : true
      ];

      const allFieldsFilled = requiredFields.every(field => field);

      if (allFieldsFilled) {
        atcButton.removeAttribute("disabled");
        requiredMessage.classList.remove('active');
      } else {
        atcButton.setAttribute("disabled", "true");
        requiredMessage.classList.add('active');
      }
    }

  }

  if (bespokeHeightInput) {
    bespokeHeightInput.addEventListener("input", updateATCButton);
  }

  if (bespokeWidthInput) {
    bespokeWidthInput.addEventListener("input", updateATCButton);
  }

  if (customWordingInput) {
    customWordingInput.addEventListener("input", updateATCButton);
  }

  if (bespokeRalStep && !bespokeRalStep.classList.contains('hide-step')) {
    bespokeRalInput.addEventListener("input", updateATCButton);
  }

  updateATCButton();
}

function pedestrianGateProperties() {
  const bespokeProperty = document.querySelector('.bespoke-height-property');
  const gateHeightSelected = document.querySelector('[data-selected-swatch-value="gate height"]').textContent.trim();
  
  const gateHandleProperty = document.querySelector('.gate-handle-property');
  const gateLetterboxProperty = document.querySelector('.gate-letterbox-property');
  
  const gateHandleOptions = document.querySelectorAll('input[name="smart-handle-options"]');
  const gateLetterboxOptions = document.querySelectorAll('input[name="steel-letterbox-options"]');

  if(gateHeightSelected) {
    if(gateHeightSelected.includes('cm')) {
      var heightNoCM = parseFloat(gateHeightSelected.replace('cm', ''));
      var cmtoMM = heightNoCM * 10;

      bespokeProperty.value = cmtoMM;
    }
  }

  gateHandleOptions.forEach(option => {
    option.addEventListener('change', () => {
      if(option.value === 'Yes' || option.value === 'yes' || option.value === 'YES') {
        gateHandleProperty.value = 'Digital Smart Handle';
      } else {
        gateHandleProperty.value = 'Standard Handle';
      }
    });
  });
  
  gateLetterboxOptions.forEach(option => {
    option.addEventListener('change', () => {
      gateLetterboxProperty.value = option.value;
    });
  });
}

function pedestrianGateAdditionalPost() {
  document.querySelectorAll('input[name="additional-post-quantity"]').forEach(function(input) {
    input.addEventListener("change", function () {
      document.querySelector(".additional-post-extra-option").setAttribute("data-product-quantity", input.value);
      buildTotalCost();
    });
  });
}

function pedestrianGateAdditionalPostSelection() {
  const additionalPostInput = document.querySelector('.additional-post-extra-option');
  const frameColourSelected = document.querySelector('[data-selected-swatch-value="frame colour"]').textContent.trim();
  const heightSelected = document.querySelector('[data-selected-swatch-value="gate height"]').textContent.trim();
  const bespokeWidthInput = document.querySelector('.gate_bespoke_width');
  const bespokeWidth = parseFloat(bespokeWidthInput.value);
  var postHeight = null;

  if(bespokeWidth >= 1350 && bespokeWidth <= 1800) {

    if(heightSelected.includes('cm')) {
      var heightMapping = {
          "90cm": "160cm",
          "120cm": "190cm",
          "150cm": "220cm",
          "180cm": "250cm",
          "210cm": "280cm"
      };
  
      postHeight = heightMapping[heightSelected];
    } else {
      var heightInput = document.querySelector('.gate_bespoke_height').value;
      function getGatePostHeight(height) {
          if (height >= 800 && height <= 1450) return "220cm";
          if (height >= 1451 && height <= 1750) return "250cm";
          if (height >= 1751 && height <= 2100) return "280cm";
          return null;
      }
  
      postHeight = getGatePostHeight(heightInput);
    }

    document.querySelectorAll('.aluminium-gate-post-product option').forEach(function(option) {
      if(option.textContent.includes(postHeight) && option.textContent.includes(frameColourSelected)) {
        additionalPostInput.setAttribute('data-product-variant', option.value);
        additionalPostInput.setAttribute('data-product-cost', option.getAttribute('data-price'));
      }
    });
  } else {
    if(heightSelected.includes('cm')) {
      var heightMapping = {
          "90cm": "160cm",
          "120cm": "190cm",
          "150cm": "220cm",
          "180cm": "250cm",
          "210cm": "280cm"
      };
  
      postHeight = heightMapping[heightSelected];
    } else {
      var heightInput = document.querySelector('.gate_bespoke_height').value;
      function getGatePostHeight(height) {
          if (height >= 800 && height <= 1000) return "160cm";
          if (height >= 1001 && height <= 1300) return "190cm";
          if (height >= 1301 && height <= 1600) return "220cm";
          if (height >= 1601 && height <= 1900) return "250cm";
          if (height >= 1901 && height <= 2100) return "280cm";
          return null;
      }
  
      postHeight = getGatePostHeight(heightInput);
    }

    if(frameColourSelected.includes('Grey')) {
      document.querySelectorAll('.grey-aluminium-fence-post-product option').forEach(function(option) {
        if(option.textContent.includes(postHeight)) {
          additionalPostInput.setAttribute('data-product-variant', option.value);
          additionalPostInput.setAttribute('data-product-cost', option.getAttribute('data-price'));
        }
      });
    } else {
      document.querySelectorAll('.black-aluminium-fence-post-product option').forEach(function(option) {
        if(option.textContent.includes(postHeight)) {
          additionalPostInput.setAttribute('data-product-variant', option.value);
          additionalPostInput.setAttribute('data-product-cost', option.getAttribute('data-price'));
        }
      });
    }
  }

  buildTotalCost();
}

function updateSlatColourProperty() {
  const slatColourProperty = document.querySelector('.slat-colour-property');
  
  // Extract the product handle from the current page URL
  const pathname = window.location.pathname;
  
  if(pathname.includes('aluminium') && pathname.includes('driveway')) {
    const slatColourSelected = document.querySelector('[data-selected-swatch-value="slat colour"]');

    if(slatColourSelected) {
      const slatColourValue = slatColourSelected.textContent.trim();
      slatColourProperty.value = slatColourValue;
    }
  } else if (pathname.includes('aspen') && pathname.includes('driveway')) {
    const slatColourSelected = document.querySelector('[data-selected-swatch-value="slat colour"]');

    if(slatColourSelected) {
      const slatColourValue = slatColourSelected.textContent.trim();
      slatColourProperty.value = slatColourValue;
    }
  } else if(pathname.includes('linea') && pathname.includes('driveway')) {
    const slatColourSelected = document.querySelector('[data-selected-swatch-value="slat colour"]');

    if(slatColourSelected) {
      const slatColourValue = slatColourSelected.textContent.trim();
      slatColourProperty.value = slatColourValue;
    }
  } else {
    const slatColourSelected = document.querySelector('[data-selected-swatch-value="slat colour"]');

    if(slatColourSelected) {
      const slatColourValue = slatColourSelected.textContent.trim();
      slatColourProperty.value = slatColourValue;
    }
  }

  if(pathname.includes('aluminium') && pathname.includes('pedestrian')) {
    if(pathname.includes('salone') || pathname.includes('louvre') || pathname.includes('aria') || pathname.includes('toledo') || pathname.includes('albany') || pathname.includes('avalon')) {
    } else {
      const slatColourSelected = document.querySelector('[data-selected-swatch-value="frame colour"]');

      if(slatColourSelected) {
        const slatColourValue = slatColourSelected.textContent.trim();
        slatColourProperty.value = slatColourValue;
      }
    }
  } else if (pathname.includes('aspen') && pathname.includes('pedestrian')) {
    const slatColourSelected = document.querySelector('[data-selected-swatch-value="screen colour"]');

    if(slatColourSelected) {
      const slatColourValue = slatColourSelected.textContent.trim();
      slatColourProperty.value = slatColourValue;
    }
  } else if(pathname.includes('linea') && pathname.includes('pedestrian')) {
    const slatColourSelected = document.querySelector('[data-selected-swatch-value="colour"]');

    if(slatColourSelected) {
      const slatColourValue = slatColourSelected.textContent.trim();
      slatColourProperty.value = slatColourValue;
    }
  } else {
    const slatColourSelected = document.querySelector('[data-selected-swatch-value="slat colour"]');

    if(slatColourSelected) {
      const slatColourValue = slatColourSelected.textContent.trim();
      slatColourProperty.value = slatColourValue;
    }
  }
}

//
//  LM: Driveway Gate Functions
//

function updateDrivewayGateBespokePricing() {
  const bespokeHeightInput = document.querySelector('.gate_bespoke_height');
  const bespokeWidthInput = document.querySelector('.gate_bespoke_width');
  const bespokeGateMinPricing = document.querySelector('.bespoke-gate-min-pricing');
  const bespokeGatePricing = document.querySelector('.bespoke-gate-pricing');
  const moneyFormat = FoxTheme.settings.moneyFormat;
  const mainProductPriceContainer = document.querySelector('.product__block--price .f-price');
  const mainProductPrice = document.querySelectorAll('.product__block--price .f-price-item');
  const mainProductComparePrice = document.querySelector('.product__block--price .f-price__sale .compare-price');
  const bespokeGateProduct = document.querySelector('.bespoke-driveway-gate-product');

  const bespokeHeightInputMin = parseFloat(bespokeHeightInput.getAttribute('min'));
  const bespokeHeightInputMax = parseFloat(bespokeHeightInput.getAttribute('max'));
  const bespokeWidthInputMin = parseFloat(bespokeWidthInput.getAttribute('min'));
  const bespokeWidthInputMax = parseFloat(bespokeWidthInput.getAttribute('max'));
  
  let bespokePrice = 0;
  let bespokeComparePrice = 0;

  // If the bespoke height & width is populated
  if (bespokeHeightInput.value && bespokeWidthInput.value) {
    var bespokeHeight = parseFloat(bespokeHeightInput.value);
    var bespokeWidth = parseFloat(bespokeWidthInput.value);
    const dataPrice = parseFloat(bespokeGatePricing.getAttribute('data-price'));
    const dataComparePrice = parseFloat(bespokeGatePricing.getAttribute('data-compare-price'));
    const bespokeMinPricing = parseFloat(bespokeGateMinPricing.getAttribute('data-price'));

    if(bespokeHeight < bespokeHeightInputMin) {
      bespokeHeight = bespokeHeightInputMin;
    } else if (bespokeHeight > bespokeHeightInputMax) {
      bespokeHeight = bespokeHeightInputMax;
    } 

    if(bespokeWidth < bespokeWidthInputMin) {
      bespokeWidth = bespokeWidthInputMin;
    } else if (bespokeWidth > bespokeWidthInputMax) {
      bespokeWidth = bespokeWidthInputMax;
    }

    // Get the total area in cm², height and width are in mm so we divide by 100
    let total_area = bespokeHeight * bespokeWidth;
    total_area = Math.round(total_area / 100);
    total_area_quantity = Math.round(total_area / 10);

    // Adjust prices to reflect the price per cm² (divide by 10)
    const adjustedDataPrice = dataPrice / 10;
    const adjustedDataComparePrice = dataComparePrice / 10;

    // Calculate the bespoke pricing based on total area (price per cm²)
    const bespokePriceCalc = adjustedDataPrice * total_area;

    // Adjust the compare price similarly
    const bespokeComparePriceCalc = adjustedDataComparePrice * total_area;

    // If the bespoke pricing is more than the minimum pricing, use the new custom amounts otherwise fall back to minimum pricing.
    if(bespokePriceCalc > bespokeMinPricing) {
      bespokePrice = bespokePriceCalc;
      bespokeComparePrice = bespokeComparePriceCalc;

      // Driveway gates are by standard £1375.00, so we need to calculate the difference between the bespoke price and the minimum pricing and add that quantity of the bespoke product.
      var price_difference = bespokePriceCalc - bespokeMinPricing;
          adjustedQuantity = price_difference / dataPrice;

      bespokeGateProduct.setAttribute('data-quantity', Math.round(adjustedQuantity));

    } else {
      // If the bespoke height is NOT populated, use the minimum pricing
      bespokePrice = parseFloat(bespokeGateMinPricing.getAttribute('data-price'));
      bespokeComparePrice = parseFloat(bespokeGateMinPricing.getAttribute('data-compare-price'));
      bespokeGateProduct.setAttribute('data-quantity', 0);
    }
  } else {
    // If the bespoke height is NOT populated, use the minimum pricing
    bespokePrice = parseFloat(bespokeGateMinPricing.getAttribute('data-price'));
    bespokeComparePrice = parseFloat(bespokeGateMinPricing.getAttribute('data-compare-price'));
    bespokeGateProduct.setAttribute('data-quantity', 0);
  }

  mainProductPriceContainer.classList.add('f-price--on-sale');

  const formattedPrice = FoxTheme.Currency.formatMoney(bespokePrice, moneyFormat);
  const formattedComparePrice = FoxTheme.Currency.formatMoney(bespokeComparePrice, moneyFormat);

  mainProductPrice.forEach(price => {
    price.textContent = formattedPrice;
    price.setAttribute('data-price', bespokePrice);
  });

  mainProductComparePrice.textContent = formattedComparePrice;
  mainProductComparePrice.setAttribute('data-compare', bespokeComparePrice);

  var ralProduct = document.querySelector('.bespoke-ral-product');
  if(ralProduct){
    var ralUpdatedQuantity = Math.round(bespokePrice * 0.1);
    ralProduct.setAttribute('data-quantity', ralUpdatedQuantity);
  }
  
  buildTotalCost(bespokePrice, bespokeComparePrice);
}

function drivewayGatesEntryChecks() {
  const bespokeHeightInput = document.querySelector(".gate_bespoke_height");
  const bespokeWidthInput = document.querySelector(".gate_bespoke_width");
  const atcButton = document.querySelector(".product-form__submit");
  const requiredMessage = document.querySelector('.required-message');
  const customWordingInput = document.querySelector('.custom-wording-input');
  const bespokeRalStep = document.querySelector('.bespoke-ral-code-input-block');
  const bespokeRalInput = document.querySelector('.custom-ral-code-input');

  function updateATCButton() {
    if (bespokeHeightInput) {
      var heightFilled = bespokeHeightInput.value.trim() !== "";
    }

    if (bespokeWidthInput) {
      var widthFilled = bespokeWidthInput.value.trim() !== "";
    }

    if (customWordingInput) {
      var customWordingFilled = customWordingInput.value.trim() !== "";
    }

    if (bespokeRalInput) {
      var bespokeRalFilled = bespokeRalInput.value.trim() !== "";
    }

    if(customWordingInput) {
      const requiredFields = [
        heightFilled,
        widthFilled,
        customWordingFilled,
        bespokeRalStep ? (!bespokeRalStep.classList.contains('hide-step') ? bespokeRalFilled : true) : true
      ];

      const allFieldsFilled = requiredFields.every(field => field);

      if (allFieldsFilled) {
        atcButton.removeAttribute("disabled");
        requiredMessage.classList.remove('active');
      } else {
        atcButton.setAttribute("disabled", "true");
        requiredMessage.classList.add('active');
      }
    } else {
      const requiredFields = [
        heightFilled,
        widthFilled,
        bespokeRalStep ? (!bespokeRalStep.classList.contains('hide-step') ? bespokeRalFilled : true) : true
      ];

      const allFieldsFilled = requiredFields.every(field => field);

      if (allFieldsFilled) {
        atcButton.removeAttribute("disabled");
        requiredMessage.classList.remove('active');
      } else {
        atcButton.setAttribute("disabled", "true");
        requiredMessage.classList.add('active');
      }
    }
  }

  if (bespokeHeightInput) {
    bespokeHeightInput.addEventListener("input", updateATCButton);
  }

  if (bespokeWidthInput) {
    bespokeWidthInput.addEventListener("input", updateATCButton);
  }

  if (customWordingInput) {
    customWordingInput.addEventListener("input", updateATCButton);
  }

  if (bespokeRalStep && !bespokeRalStep.classList.contains('hide-step')) {
    bespokeRalInput.addEventListener("input", updateATCButton);
  }

  updateATCButton();
}

function checkAutomationKitSelection() {
  const container = document.querySelector('.variant-step-content.automation-content');

  if (!container) {
      return;
  }

  const selectedRadio = container.querySelector('input[type="radio"]:checked');
  const automationStep = document.querySelector('.automation-step');

  const handleStep = document.querySelector('.driveway-gate-smart-handle');
  const gateHandleProperty = document.querySelector('.gate-handle-property');

  if(handleStep) {
    var noHandleOption = handleStep.querySelector('input[value="No"]');
  }

  if (selectedRadio.value === "Installing My Own Automation Kit") {
      if(automationStep) {
        automationStep.classList.remove('hide-step');
        const firstRadio = automationStep.querySelector('input[type="radio"]');
        if (firstRadio) {
            firstRadio.checked = true;
        }
      }

      if(handleStep) {
        handleStep.classList.add('hide-step');
        if(noHandleOption) {
          noHandleOption.click();
          noHandleOption.dispatchEvent(new Event('change'));
        }
        gateHandleProperty.value = '';
      }
  } else if (selectedRadio.value === "No Automation") {
      if(automationStep) {
        automationStep.classList.add('hide-step');
      }

      if(handleStep) {
        handleStep.classList.remove('hide-step');
        gateHandleProperty.value = 'Standard Handle';
        if(noHandleOption) {
          noHandleOption.click();
          noHandleOption.dispatchEvent(new Event('change'));
        }
      }
  }
}

function updateAluminiumPostImage() {
  const selectedSlatColour = document.querySelector('[data-selected-swatch-value="slat colour"]');
  if (!selectedSlatColour) return;

  const selectedSlatColourValue = selectedSlatColour.textContent.trim().toLowerCase().replace(/\s+/g, '-');

  const aluminiumPostStepOptions = document.querySelectorAll('.driveway-additional-gate-post .image');
  const bespokeRalInformation = document.querySelector('.bespoke-ral-information');
  aluminiumPostStepOptions.forEach(option => {
    if(selectedSlatColourValue == 'bespoke-ral-colour') {
      var selectedImage = option.getAttribute('data-black');
      if(bespokeRalInformation) {
        bespokeRalInformation.classList.remove('hidden');
      }
    } else {
      var selectedImage = option.getAttribute('data-' + selectedSlatColourValue);
      if(bespokeRalInformation) { 
        bespokeRalInformation.classList.add('hidden');
      }
    }

    var image = option.querySelector('img');

    if(selectedImage) {
      image.src = selectedImage;
      image.srcset = selectedImage;
    }
  });
}

function aluminiumPostSelection(width) {
  function getGatePostHeight(width) {
      if (width >= 800 && width <= 1450) return 220;
      if (width >= 1451 && width <= 1750) return 250;
      if (width >= 1751 && width <= 2100) return 280;
      return null;
  }

  updateAluminiumPostImage();

  // Get the selected color from .data-selected-swatch-value
  const selectedSwatch = document.querySelector('[data-selected-swatch-value="frame colour"]');
  if (selectedSwatch) {
    var selectedColor = selectedSwatch.textContent.trim();
  } else {
    const selectedSlatColour = document.querySelector('[data-selected-swatch-value="slat colour"]');
    if (selectedSlatColour) {
      var selectedSlatName = selectedSlatColour.textContent.trim().toLowerCase().replace(/\s+/g, '-');
  
      const aluminiumPostMapping = {
          "black": "Black",
          "anthracite": "Anthracite",
          "off-white": "Off White",
          "sage": "Sage",
          "natural-oak": "Natural Oak",
          "natural-oak-with-black-frame": "Black",
          "dark-oak": "Dark Oak",
          "dark-oak-with-black-frame": "Black",
          "teak": "Teak",
          "teak-with-black-frame": "Black",
          "bespoke-ral-colour": "Black"
      };
  
      var selectedColor = aluminiumPostMapping[selectedSlatName];
    }
  }

  const postHeight = getGatePostHeight(width);

  const selectElement = document.querySelector('.aluminium-post-product');
  if (!selectElement) return;

  let matchedOption = null;
  selectElement.querySelectorAll('option').forEach(option => {
      if (option.textContent.includes(`${postHeight}cm`) && option.textContent.includes(selectedColor)) {
          matchedOption = option;
      }
  });

  if (matchedOption) {
      matchedOption.selected = true;
      var aluminiumPostPrice = matchedOption.getAttribute('data-price'),
          aluminiumPostPriceCalc = aluminiumPostPrice * 2;
      document.querySelector('.aluminium-extra-post').setAttribute('data-product-variant', matchedOption.value);
      document.querySelector('.aluminium-extra-post').setAttribute('data-product-cost', aluminiumPostPriceCalc);

      buildTotalCost();
  }
}

function drivewayGateProperties() {
  const drivewayGateSmartHandleStep = document.querySelector('.driveway-gate-smart-handle');

  const gateHandleProperty = document.querySelector('.gate-handle-property');
  const gateLetterboxProperty = document.querySelector('.gate-letterbox-property');
  
  const gateHandleOptions = document.querySelectorAll('input[name="smart-handle-options"]');
  const gateLetterboxOptions = document.querySelectorAll('input[name="steel-letterbox-options"]');

  if(drivewayGateSmartHandleStep) {
    if(!drivewayGateSmartHandleStep.classList.contains('hide-step')) {
      gateHandleOptions.forEach(option => {
        option.addEventListener('change', () => {
          if(option.value === 'Yes' || option.value === 'yes' || option.value === 'YES') {
            gateHandleProperty.value = 'Digital Smart Handle';
          } else {
            gateHandleProperty.value = 'Standard Handle';
          }
        });
      });
    } else {
      gateHandleProperty.value = '';
    }
  }
  
  gateLetterboxOptions.forEach(option => {
    option.addEventListener('change', () => {
      gateLetterboxProperty.value = option.value;
    });
  });
}

//
//  LM: Planter Wheel Functions
//
function planterWheelSelection() {

  // Get the selected size
  const selectedSize = document.querySelector('.selected-planter-size').textContent.trim();

  // Get the selected color
  const selectedColor = document.querySelector('[data-selected-swatch-value="base colour" i], [data-selected-swatch-value="colour" i]').textContent.trim();

  const selectElement = document.querySelector('.planter-wheels-product');
  if (!selectElement) return;

  let matchedOption = null;
  selectElement.querySelectorAll('option').forEach(option => {
      if (option.textContent.trim() === selectedSize + ' / ' + selectedColor) {
          matchedOption = option;
      }
  });

  if (matchedOption) {
      matchedOption.selected = true;
      var wheelPrice = matchedOption.getAttribute('data-price');
      document.querySelector('.wheel-extra-option').setAttribute('data-product-variant', matchedOption.value);
      document.querySelector('.wheel-extra-option').setAttribute('data-product-cost', wheelPrice);

      buildTotalCost();
  }
}

//
//  LM: Bin Storage Functions
//

function binStorageEntryChecks() {
  const termsCheckbox = document.querySelector(".bin-storage-terms-checkbox");
  const atcButton = document.querySelector(".product-form__submit");

  function updateATCButton() {
    const termsChecked = termsCheckbox.checked;

    if (termsChecked) {
      atcButton.removeAttribute("disabled");
    } else {
      atcButton.setAttribute("disabled", "true");
    }
  }

  termsCheckbox.addEventListener("change", updateATCButton);

  updateATCButton();
}

//
//  LM: Decorative Panels Functions
//

function decorativePanelsEntryChecks() {
  const bespokeWidthInput = document.querySelector(".panel_bespoke_width");
  const termsCheckboxWrapper = document.querySelector(".terms-checkbox");
  const termsCheckbox = document.querySelector(".dp-terms-checkbox");
  const variantSelect = document.querySelector("input[value='BESPOKE SIZE']");
  const bespokeHeightInput = document.querySelector(".panel_bespoke_height");
  const atcButton = document.querySelector(".product-form__submit");
  const requiredMessage = document.querySelector('.required-message');

  function updateATCButton() {
    if (bespokeHeightInput) {
      var heightFilled = bespokeHeightInput.value.trim() !== "";
    }

    if (bespokeWidthInput) {
      var widthFilled = bespokeWidthInput.value.trim() !== "";
    }

    if (termsCheckbox) {
      var termsChecked = termsCheckbox.checked;
    }

    if(variantSelect && variantSelect.checked) {
      if (termsCheckboxWrapper) {
        termsCheckboxWrapper.classList.remove("hide-step");
      }

      if (heightFilled && widthFilled && termsChecked) {
          atcButton.removeAttribute("disabled");
          requiredMessage.classList.remove('active');
      } else {
          atcButton.setAttribute("disabled", "true");
          requiredMessage.classList.add('active');
      }
    } else {
      atcButton.removeAttribute("disabled");
      requiredMessage.classList.remove('active');
      if (termsCheckboxWrapper) {
        termsCheckboxWrapper.classList.add("hide-step");
      }
    }
  }

  if (bespokeHeightInput) { 
    bespokeHeightInput.addEventListener("input", updateATCButton);
  }

  if (bespokeWidthInput) {
    bespokeWidthInput.addEventListener("input", updateATCButton);
  }

  if (termsCheckbox) {
    termsCheckbox.addEventListener("change", updateATCButton);
  }

  updateATCButton();
}

function updateDecorativePanelBespokePricing() {
  const bespokeSizeInput = document.querySelector('input[value="BESPOKE SIZE"]');
  const bespokeHeightInput = document.querySelector('.panel_bespoke_height');
  const bespokeWidthInput = document.querySelector('.panel_bespoke_width');
  const moneyFormat = FoxTheme.settings.moneyFormat;

  const mainProductPriceContainer = document.querySelector('.product__block--price .f-price');
  const mainProductPrice = document.querySelectorAll('.product__block--price .f-price-item');
  const mainProductComparePrice = document.querySelector('.product__block--price .f-price__sale .compare-price');
  const bespokePanelProduct = document.querySelector('.bespoke-product');

  const thickness = document.querySelector('[data-selected-swatch-value="thickness"]').textContent.trim();
  let dataPrice = 0;
  let dataComparePrice = 0;
  let bespokeMinPricing = 0;
  let bespokeMinComparePricing = 0;
  let bespokePanelProductVariant = '';
  let bespokePanelProductQuantity = 0;

  const bespokeHeightInputMin = parseFloat(bespokeHeightInput.getAttribute('min'));
  const bespokeHeightInputMax = parseFloat(bespokeHeightInput.getAttribute('max'));
  const bespokeWidthInputMin = parseFloat(bespokeWidthInput.getAttribute('min'));
  const bespokeWidthInputMax = parseFloat(bespokeWidthInput.getAttribute('max'));

  document.querySelectorAll('.bespoke-panel-select option').forEach(option => {
    if (option.textContent.includes(thickness)) {
      dataPrice = parseFloat(option.getAttribute('data-price'));
      dataComparePrice = parseFloat(option.getAttribute('data-compare-price'));
      bespokeMinPricing = parseFloat(option.getAttribute('data-min-price'));
      bespokeMinComparePricing = parseFloat(option.getAttribute('data-min-compare-price'));
      bespokePanelProductVariant = option.value;
      bespokePanelProductQuantity = parseFloat(option.getAttribute('data-quantity'));
    }
  });
  
  let bespokePrice = 0;
  let bespokeComparePrice = 0;

  if (bespokeSizeInput && bespokeSizeInput.checked) {
    // If the bespoke height & width is populated
    if (bespokeHeightInput.value && bespokeWidthInput.value) {
      var bespokeHeight = parseFloat(bespokeHeightInput.value);
      var bespokeWidth = parseFloat(bespokeWidthInput.value);

      if(bespokeHeight < bespokeHeightInputMin) {
        bespokeHeight = bespokeHeightInputMin;
      } else if (bespokeHeight > bespokeHeightInputMax) {
        bespokeHeight = bespokeHeightInputMax;
      }

      if(bespokeWidth < bespokeWidthInputMin) {
        bespokeWidth = bespokeWidthInputMin;
      } else if (bespokeWidth > bespokeWidthInputMax) {
        bespokeWidth = bespokeWidthInputMax;
      }
      
      // Get the total area in cm², height and width are in cm alredy so we just divide by 10
      let total_area = bespokeHeight * bespokeWidth;
      total_area = Math.round(total_area / 10);

      // Calculate the bespoke pricing based on total area (price per cm²)
      const bespokePriceCalc = dataPrice * total_area;

      // Adjust the compare price similarly
      const bespokeComparePriceCalc = dataComparePrice * total_area;

      // If the bespoke pricing is more than the minimum pricing, use the new custom amounts otherwise fall back to minimum pricing.
      if(bespokePriceCalc > bespokeMinPricing) {
        bespokePrice = bespokePriceCalc;
        bespokeComparePrice = bespokeComparePriceCalc;
        bespokePanelProduct.setAttribute('data-variant', bespokePanelProductVariant);
        bespokePanelProduct.setAttribute('data-quantity', total_area);
      } else {
        // If the bespoke height is NOT populated, use the minimum pricing
        bespokePrice = bespokeMinPricing;
        bespokeComparePrice = bespokeMinComparePricing;
        bespokePanelProduct.setAttribute('data-variant', bespokePanelProductVariant);
        bespokePanelProduct.setAttribute('data-quantity', bespokePanelProductQuantity);
      }
    } else {
      // If the bespoke height is NOT populated, use the minimum pricing
      bespokePrice =bespokeMinPricing;
      bespokeComparePrice = bespokeMinComparePricing;
      bespokePanelProduct.setAttribute('data-variant', bespokePanelProductVariant);
      bespokePanelProduct.setAttribute('data-quantity', bespokePanelProductQuantity);
    }

    mainProductPriceContainer.classList.add('f-price--on-sale');

    const formattedPrice = FoxTheme.Currency.formatMoney(bespokePrice, moneyFormat);
    const formattedComparePrice = FoxTheme.Currency.formatMoney(bespokeComparePrice, moneyFormat);

    mainProductPrice.forEach(price => {
      price.textContent = formattedPrice;
      price.setAttribute('data-price', bespokePrice);
    });

    if(bespokeComparePrice > 0) {
      mainProductComparePrice.textContent = formattedComparePrice;
      mainProductComparePrice.setAttribute('data-compare', bespokeComparePrice);
    } else {
      mainProductComparePrice.textContent = '';
      mainProductComparePrice.setAttribute('data-compare', '');
    }
    
    buildTotalCost(bespokePrice, bespokeComparePrice)
  }
}

function decorativePanelProperties() {
  const screenThicknessProperty = document.querySelector('.screen-thickness-property');
  const screenColourProperty = document.querySelector('.screen-colour-property');

  // Get the selected thickness from .data-selected-swatch-value
  const thickness = document.querySelector('[data-selected-swatch-value="thickness"]').textContent.trim();
  screenThicknessProperty.value = thickness;

  // Get the selected color from .data-selected-swatch-value
  const selectedColourSwatch = document.querySelector('[data-selected-swatch-value="colour"]').textContent.trim();
  screenColourProperty.value = selectedColourSwatch;
}

//
//  LM: InsuSlat Wall Functions
//

function insuSlatBattenSelection() {
  const battenOptions = document.querySelectorAll('input[name="batten-option"]');
  const battenProductInput = document.querySelector('input[name="selected-batten-product"]');
  const battenProducts = document.querySelector('script[data-batten-products]');
  const battenData = battenProducts ? JSON.parse(battenProducts.textContent) : null;
  const battenLengthOptions = document.querySelectorAll('input[name="batten-length-option"]');
  const extraBattenProductInput = document.querySelector('input[name="selected-extra-batten-product"]');
  const extraBattenLengthOptions = document.querySelectorAll('input[name="extra-batten-length-option"]');

  battenOptions.forEach(option => {
    option.addEventListener('change', function() {
      if(option.value != 'No Batten') {
        document.querySelector('.batten-length-outer-wrapper').classList.remove('hidden');

        var batten_type = option.getAttribute('data-type').toLowerCase();
        var batten_colour = option.getAttribute('data-colour')?.toLowerCase();
        var batten_led = option.getAttribute('data-led');
        var batten_length = document.querySelector('input[name="batten-length-option"]:checked').value;

        var battenTitleCheck = batten_colour ? batten_type + ' - ' + batten_colour : batten_type;
        const battenProduct = battenData.find(product => product.title.toLowerCase().includes(battenTitleCheck) && product.variants.find(variant => variant.title.toLowerCase().includes(batten_led.toLowerCase())));

        if(battenProduct) {
          const matchingVariant = battenProduct.variants.find(variant => 
            variant.title.toLowerCase().includes(batten_led.toLowerCase()) && 
            variant.title.toLowerCase().includes(batten_length.toLowerCase())
          );
          
          if (matchingVariant) {
            battenProductInput.setAttribute('data-product-variant', matchingVariant.id);
            battenProductInput.setAttribute('data-price', matchingVariant.price);
            battenProductInput.setAttribute('data-compare-price', matchingVariant.compare_at_price);
          }
        }

        if(option.value.includes('top-trim')) {
          document.querySelector('.extra-batten-length-outer-wrapper').classList.remove('hidden');
        } else {
          document.querySelector('.extra-batten-length-outer-wrapper').classList.add('hidden');
          extraBattenProductInput.setAttribute('data-product-variant', '');
          extraBattenProductInput.setAttribute('data-price', '');
          extraBattenProductInput.setAttribute('data-compare-price', '');
          document.querySelector('#no-extra-batten').checked = true;
        }
      } else {
        document.querySelector('.batten-length-outer-wrapper').classList.add('hidden');
        battenProductInput.setAttribute('data-product-variant', '');
        battenProductInput.setAttribute('data-price', '');
        battenProductInput.setAttribute('data-compare-price', '');

        document.querySelector('.extra-batten-length-outer-wrapper').classList.add('hidden');
        extraBattenProductInput.setAttribute('data-product-variant', '');
        extraBattenProductInput.setAttribute('data-price', '');
        extraBattenProductInput.setAttribute('data-compare-price', '');
        document.querySelector('#no-extra-batten').checked = true;
      }

      buildTotalCost();
    });
  });

  battenLengthOptions.forEach(option => {
    option.addEventListener('change', function() {

      var batten_type = document.querySelector('input[name="batten-option"]:checked').getAttribute('data-type').toLowerCase();
      var batten_colour = document.querySelector('input[name="batten-option"]:checked').getAttribute('data-colour')?.toLowerCase();
      var batten_led = document.querySelector('input[name="batten-option"]:checked').getAttribute('data-led');
      var batten_length = option.value;

      var battenTitleCheck = batten_colour ? batten_type + ' - ' + batten_colour : batten_type;
      const battenProduct = battenData.find(product => product.title.toLowerCase().includes(battenTitleCheck) && product.variants.find(variant => variant.title.toLowerCase().includes(batten_led.toLowerCase())));

      if(battenProduct) {
        const matchingVariant = battenProduct.variants.find(variant => 
          variant.title.toLowerCase().includes(batten_led.toLowerCase()) && 
          variant.title.toLowerCase().includes(batten_length.toLowerCase())
        );
        
        if (matchingVariant) {
          battenProductInput.setAttribute('data-product-variant', matchingVariant.id);
          battenProductInput.setAttribute('data-price', matchingVariant.price);
          battenProductInput.setAttribute('data-compare-price', matchingVariant.compare_at_price);
        }
      }

      buildTotalCost();
    });
  });

  extraBattenLengthOptions.forEach(option => {
    option.addEventListener('change', function() {
      if(option.value != 'No Extra Batten') {
        var batten_type = document.querySelector('input[name="batten-option"]:checked').getAttribute('data-type').toLowerCase();
        var batten_colour = document.querySelector('input[name="batten-option"]:checked').getAttribute('data-colour')?.toLowerCase();
        var batten_led = document.querySelector('input[name="batten-option"]:checked').getAttribute('data-led');
        var batten_length = option.value;

        var battenTitleCheck = batten_colour ? batten_type + ' - ' + batten_colour : batten_type;
        const battenProduct = battenData.find(product => product.title.toLowerCase().includes(battenTitleCheck) && product.variants.find(variant => variant.title.toLowerCase().includes(batten_led.toLowerCase())));

        if(battenProduct) {
          const extraBattenMatchingVariant = battenProduct.variants.find(variant => 
            variant.title.toLowerCase().includes(batten_led.toLowerCase()) && 
            variant.title.toLowerCase().includes(batten_length.toLowerCase())
          );
          
          if (extraBattenMatchingVariant) {
            extraBattenProductInput.setAttribute('data-product-variant', extraBattenMatchingVariant.id);
            extraBattenProductInput.setAttribute('data-price', extraBattenMatchingVariant.price);
            extraBattenProductInput.setAttribute('data-compare-price', extraBattenMatchingVariant.compare_at_price);
          }
        }
      } else {
        extraBattenProductInput.setAttribute('data-product-variant', '');
        extraBattenProductInput.setAttribute('data-price', '');
        extraBattenProductInput.setAttribute('data-compare-price', '');
      }

      buildTotalCost();
    });
  });
}

function stripLightSelection() {
  const stripLightOptions = document.querySelectorAll('input[name="strip-lights-option"]');
  const stripLightSizeOptions = document.querySelectorAll('input[name="strip-light-size"]');
  const stripLightPackOptions = document.querySelectorAll('.variant-option-label-strip-light');
  const stripLightPackOptionsInputs = document.querySelectorAll('input[name="strip-light-variant"]');

  stripLightOptions.forEach(option => {
    option.addEventListener('change', function() {
      if(option.value != 'No Strip Lights') {
        document.querySelector('.strip-light-product').classList.remove('hidden');
      } else {
        document.querySelector('.strip-light-product').classList.add('hidden');
      }

      buildTotalCost();
    });
  });

  stripLightSizeOptions.forEach(option => {
    option.addEventListener('change', function() {
      const stripLightSize = option.getAttribute('data-size');
      const checkedPackOption = document.querySelector('input[name="strip-light-variant"]:checked');
      const checkedPackSize = checkedPackOption.getAttribute('data-pack-size');

      stripLightPackOptions.forEach(packOption => {
        if(packOption.getAttribute('data-size') != stripLightSize) {
          packOption.classList.add('!hidden');
        } else {
          packOption.classList.remove('!hidden');
        }
      });

      stripLightPackOptionsInputs.forEach(packOptionInput => {
        if(packOptionInput.getAttribute('data-pack-size') == checkedPackSize && packOptionInput.getAttribute('data-size') == stripLightSize) {
          packOptionInput.checked = true;
        } else {
          packOptionInput.checked = false;
        }
      });

      buildTotalCost();
    });
  });

  stripLightPackOptionsInputs.forEach(packOptionInput => {
    packOptionInput.addEventListener('change', function() {
      buildTotalCost();
    });
  });
}

function insuslatShelvingSelection() {
  const shelvingOptions = document.querySelectorAll('input[name="shelving-option"]');

  shelvingOptions.forEach(option => {
    option.addEventListener('change', function() {
      if(option.value != 'No Shelving') {
        document.querySelector('.shelving-product').classList.remove('hidden');
      } else {
        document.querySelector('.shelving-product').classList.add('hidden');
      }

      buildTotalCost();
    });
  });
}

function insuslatEntryChecks() {
  const bespokeHeightInput = document.querySelector(".insuslat-bespoke-height");
  const termsCheckbox = document.querySelector(".insuslat-terms-checkbox");
  const variantSelect = document.querySelector("input[value='Yes']");
  const atcButton = document.querySelector(".product-form__submit");
  const requiredMessage = document.querySelector('.required-message');

  function updateATCButton() {
    if (bespokeHeightInput) {
      var heightFilled = bespokeHeightInput.value.trim() !== "";
    }

    if (termsCheckbox) {
      var termsChecked = termsCheckbox.checked;
    }

    if(variantSelect && variantSelect.checked) {
      if (heightFilled && termsChecked) {
          atcButton.removeAttribute("disabled");
          requiredMessage.classList.remove('active');
      } else {
          atcButton.setAttribute("disabled", "true");
          requiredMessage.classList.add('active');
      }
    }
  }

  if (bespokeHeightInput) {
    bespokeHeightInput.addEventListener("input", updateATCButton);
  }

  if (termsCheckbox) {
    termsCheckbox.addEventListener("change", updateATCButton);
  }

  updateATCButton();
}

//
//  LM: Mirror Lighting Functions
//
function mirrorLightingSelection() {

  const selectedSize = document.querySelector('[data-selected-swatch-value="size"]').textContent.trim();

  const selectElement = document.querySelector('.mirror-lighting-product');
  if (!selectElement) return;

  const powerSupplyMapping = {
      "200cm x 116cm": "60W",
      "200cm x 114cm": "60W",
      "200cm x 110cm": "60W",
      "198cm x 110cm": "60W",
      "198cm x 108cm": "60W",
      "186cm x 92cm": "60W",
      "186cm x 90cm": "60W",
      "184cm x 94cm": "60W",
      "184cm x 92cm": "60W",
      "186cm x 70cm": "48W",
      "182cm x 72cm": "48W",
      "182cm x 70cm": "48W",
      "124cm x 96cm": "36W",
      "124cm x 90cm": "36W",
      "96cm x 124cm": "36W",
      "92cm x 114cm": "36W"
  };

  var selectedPowerSupply = powerSupplyMapping[selectedSize];

  let matchedOption = null;
  selectElement.querySelectorAll('option').forEach(option => {
      if (option.textContent.trim() === selectedPowerSupply) {
          matchedOption = option;
      }
  });

  if (matchedOption) {
      matchedOption.selected = true;
      var mirrorLightingPrice = matchedOption.getAttribute('data-price');
      document.querySelector('.mirror-lighting-extra-option').setAttribute('data-product-variant', matchedOption.value);
      document.querySelector('.mirror-lighting-extra-option').setAttribute('data-product-cost', mirrorLightingPrice);

      buildTotalCost();
  }
}

function mirrorLEDImageSelection() {
  // Find the media gallery element
  const mediaGallery = document.querySelector('media-gallery');
  if (!mediaGallery || !mediaGallery.sliderInstance) return;
  
  const slider = mediaGallery.sliderInstance.slider;
  const currentIndex = slider.realIndex;
  
  // Get all thumbnail elements (they don't have loops, so no duplicates)
  const thumbnailContainer = mediaGallery.querySelector('[id^="GalleryThumbnails"]');
  if (!thumbnailContainer) return;
  
  const thumbnails = Array.from(thumbnailContainer.querySelectorAll('.product__thumbs-item'));
  const totalThumbnails = thumbnails.length;
  
  if (currentIndex < 0 || currentIndex >= totalThumbnails) return;
  
  if(document.querySelector('input#yes-mirror-lighting:checked')) {
    // Check next thumbnail (offset 1)
    for (let offset = 1; offset <= 2 && offset < totalThumbnails; offset++) {
      const targetIndex = currentIndex + offset;
      if (targetIndex >= totalThumbnails) break;
      
      const thumbnail = thumbnails[targetIndex];
      const img = thumbnail ? thumbnail.querySelector('img') : null;
      const altText = img ? img.getAttribute('alt') : '';
      
      if (altText && altText.toLowerCase().includes('led')) {
        slider.slideToLoop(targetIndex);
        return;
      }
    }
  } else if(document.querySelector('input#no-mirror-lighting:checked')) {
    // Check previous thumbnails (offset -1, then -2)
    for (let offset = 1; offset <= 2 && offset < totalThumbnails; offset++) {
      const targetIndex = currentIndex - offset;
      if (targetIndex < 0) break;
      
      const thumbnail = thumbnails[targetIndex];
      const img = thumbnail ? thumbnail.querySelector('img') : null;
      const altText = img ? img.getAttribute('alt') : '';
      
      if (altText && altText.toLowerCase().includes('noled')) {
        slider.slideToLoop(targetIndex);
        return;
      }
    }
  }
}

function mirrorUpdateATCButton() {
  const atcButton = document.querySelector('.product-form__submit span');

  if(atcButton) {
    atcButton.textContent = 'Pre-Order';
  }
}

function optionResets() {
  const noLighting = document.querySelector('input[value="No Lighting"]');
  const noExtras = document.querySelectorAll('.extra-option-step input[value="No"]');
  const bespokeEntries = document.querySelectorAll('.bespoke-input-entry');
  const checkboxes = document.querySelectorAll('.terms-checkbox input');
  const noBatten = document.querySelector('input[value="No Batten"]');
  const noStripLights = document.querySelector('input[value="No Strip Lights"]');
  const noShelving = document.querySelector('input[value="No Shelving"]');
  const standardTrim = document.querySelector('input[value="Standard Trim"]');

  // Use setTimeout to ensure DOM is ready
  setTimeout(() => {
    if (noLighting) {
      noLighting.checked = true;
      noLighting.dispatchEvent(new Event("change", { bubbles: true }));
    }

    if (noExtras) {
      noExtras.forEach(extra => {
        extra.checked = true;
        extra.dispatchEvent(new Event("change", { bubbles: true }));
      });
    }

    if (bespokeEntries) {
      bespokeEntries.forEach(entry => {
        entry.value = '';
      });
    }

    if (checkboxes) {
      checkboxes.forEach(checkbox => {
        checkbox.checked = false;
        checkbox.dispatchEvent(new Event("change", { bubbles: true }));
      });
    }

    if (noBatten) {
      noBatten.checked = true;
      noBatten.dispatchEvent(new Event("change", { bubbles: true }));
    }

    if (noStripLights) {  
      noStripLights.checked = true;
      noStripLights.dispatchEvent(new Event("change", { bubbles: true }));
    }

    if (noShelving) {
      noShelving.checked = true;
      noShelving.dispatchEvent(new Event("change", { bubbles: true }));
    }

    if (standardTrim) {
      standardTrim.checked = true;
      standardTrim.dispatchEvent(new Event("change", { bubbles: true }));
    }
    
  }, 500);
}

// General Function Calls
updateStepCounts();
triggerMainAddToCart();
buildTotalCost();
optionResets();
stepTooltip();
viewAllLogic();
calculateViewAllContainerHeight();
storeStepItemData();

window.addEventListener("load", function() {
  moreToStep();
});

document.addEventListener("wheel", function(event){
  if(document.activeElement.type === "number"){
      document.activeElement.blur();
  }
});

document.querySelectorAll('.product__block--buy_buttons input.quantity__input').forEach(function(quantity) {
  quantity.addEventListener('change', function() {
    buildTotalCost();

    if(document.querySelector('body').classList.contains('template-aquaslat_wall_panel')) {
      const areaCovered = document.querySelector('.meter-calculation span').getAttribute('data-area-covered');
      const calculation = quantity.value * areaCovered;
      document.querySelector('.meter-calculation span').textContent = parseFloat(calculation).toFixed(1);
    }

    if(document.querySelector('body').classList.contains('template-wall_panel')) {
      const areaCovered = document.querySelector('.meter-calculation span').getAttribute('data-area-covered');
      const calculation = quantity.value * areaCovered;
      document.querySelector('.meter-calculation span').textContent = parseFloat(calculation).toFixed(2);
    }
  });
});

document.querySelectorAll('.extra-option-step input[type="radio"]').forEach(function(radioButton) {
  radioButton.addEventListener('change', function() {
    buildTotalCost();
  });
});

// Fencing Specific Functions
if (document.querySelector('body').classList.contains('template-kit')) {
  runFenceMeasurementScript(true);
  updateFenceHeightValues();
  initializeInstallationSelection(true);
  updateSlatType();
  fencePanelTrimOptions();
  fenceSlatPlanterOptions();
  RALFence();
}

// Fencing & Decorative Fences Functions
if (document.querySelector('body').classList.contains('template-kit') || document.querySelector('body').classList.contains('template-decorative-fences')) {
  toggleLightingProduct();

  document.querySelectorAll('input[name="lighting-power-option"]').forEach(function(radioButton) {
    radioButton.addEventListener('change', function() {
      updatePowerProduct();
    });
  });
}

// Pedestrian Gates Functions
if (document.querySelector('body').classList.contains('template-pedestrian-gates')) {
  enforceBespokeInputRange();
  updatePedestrianGateBespokePricing();
  pedestrianGatesEntryChecks();
  updateSlatType();
  pedestrianGateProperties();
  updateSlatColourProperty();
  gateRalCode();
  pedestrianGateAdditionalPost();
  pedestrianGateAdditionalPostSelection();

  document.querySelector('.gate_bespoke_height').addEventListener('change', function() {
    updatePedestrianGateBespokePricing();
    pedestrianGateAdditionalPostSelection();
  });
  document.querySelector('.gate_bespoke_width').addEventListener('change', function() {
    updatePedestrianGateBespokePricing();
    pedestrianGateAdditionalPostSelection();
  });
}

// Driveway Gates Functions
if (document.querySelector('body').classList.contains('template-Gates')) {
  enforceBespokeInputRange();
  drivewayGatesEntryChecks();
  updateSlatType();
  drivewayGateProperties();
  updateSlatColourProperty();
  gateCustomWording();
  gateRalCode();
  aluminiumPostSelection();

  document.querySelector('.gate_bespoke_height').addEventListener('change', function() {
    updateDrivewayGateBespokePricing();
  });
  document.querySelector('.gate_bespoke_width').addEventListener('change', function() {
    updateDrivewayGateBespokePricing();
  });
}

// Planter Functions
if (document.querySelector('body').classList.contains('template-planters')) {
  planterWheelSelection();
}

// Bin Storage Functions
if (document.querySelector('body').classList.contains('template-bin-storage')) {
  binStorageEntryChecks();
}

// Decorative Panels Functions
if (document.querySelector('body').classList.contains('template-decorative-panels')) {
  enforceBespokeInputRange();
  decorativePanelsEntryChecks();
  updateDecorativePanelBespokePricing();
  decorativePanelProperties();

  if (document.querySelector('.panel_bespoke_height')) {
    document.querySelector('.panel_bespoke_height').addEventListener('change', function() {
      updateDecorativePanelBespokePricing();
    });
  }

  if (document.querySelector('.panel_bespoke_width')) {
    document.querySelector('.panel_bespoke_width').addEventListener('change', function() {
      updateDecorativePanelBespokePricing();
    });
  }
}

// InsuSlat Wall Functions
if (document.querySelector('body').classList.contains('template-wall_panel')) {
  insuSlatBattenSelection();
  stripLightSelection();
  insuslatShelvingSelection();
  insuslatEntryChecks();
  enforceBespokeInputRange();
}

// Primed Wall Functions
if (document.querySelector('body').classList.contains('template-primed-wall-panel')) {
  insuSlatBattenSelection();
}

// Mirror Lighting Functions
if (document.querySelector('body').classList.contains('template-mirrors')) {
  mirrorLightingSelection();

  if(!window.location.href.includes('with-led')) {
    document.querySelectorAll('input[name="mirror-lighting-options"]').forEach(function(radioButton) {
      radioButton.addEventListener('change', function() {
        mirrorLEDImageSelection();
      });
    });
  }
}

